<script setup lang="ts" generic="T">
import { ToastService } from '@/general/services/toasts/toast.service';
import { onBeforeUnmount, ref, watch } from 'vue';

interface Props<T> {
  visible: boolean;
  pollableFunction: (...params: any) => Promise<T>;
  stopCondition: (data: T) => boolean;
  failedCondition?: (data: T) => boolean;
  failedMessage?: (data: T) => string;
}

const props = defineProps<Props<T>>();
const emit = defineEmits<{
  onFinish: [];
}>();
const data = ref<T | undefined>();
const interval = ref();

onBeforeUnmount(() => {
  disableInterval();
  data.value = undefined;
});

async function poll() {
  if (props.visible) {
    const response = await props.pollableFunction();
    data.value = response;
    if (props.failedCondition?.(response)) {
      emit('onFinish');
      ToastService.getInstance().removeAllToasts();
      ToastService.getInstance().displayCustomToast({
        severity: 'error',
        group: 'pollableToast',
      });
      clearInterval(interval.value);
      return;
    }

    if (props.stopCondition(response)) {
      emit('onFinish');
      ToastService.getInstance().removeAllToasts();
      ToastService.getInstance().displayCustomToast({
        severity: 'success',
        group: 'pollableToast',
      });
      clearInterval(interval.value);
    }
  }
}

function disableInterval() {
  if (interval.value) {
    clearInterval(interval.value);
  }
}

watch(
  () => props.visible,
  () => {
    if (props.visible) {
      data.value = undefined;
      ToastService.getInstance().removeAllToasts();
      ToastService.getInstance().displayCustomToast({ severity: 'info', group: 'pollableToast' });
      interval.value = setInterval(() => poll(), 3000);
    }
  },
);
</script>

<template>
  <Toast position="bottom-right" group="pollableToast">
    <template #message>
      <slot name="data" :data="data" />
      <div v-if="data != null && failedCondition?.(data)" class="flex flex-column w-full">
        <p>{{ failedMessage?.(data) }}</p>
      </div>
    </template>
  </Toast>
</template>
